import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// Lightbox Import
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

gsap.registerPlugin(ScrollTrigger);

const GallerySection = ({ galleryImages, currentSlide, setCurrentSlide }) => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);

  // State for Lightbox
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const nextGallerySlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages?.length);
  };

  const prevGallerySlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + galleryImages?.length) % galleryImages?.length,
    );
  };

  // Function to open lightbox at specific image index
  const openLightbox = (imgIndex) => {
    setIndex(imgIndex);
    setOpen(true);
  };

  useEffect(() => {
    if (!sectionRef.current) return;

    const context = gsap.context(() => {
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          { y: 50, opacity: 0, clipPath: "inset(100% 0 0% 0)" },
          {
            y: 0,
            opacity: 1,
            clipPath: "inset(0% 0 0% 0)",
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          },
        );
      }

      const layoutContainer = sectionRef.current.querySelector(
        ".gallery-layout-container",
      );
      if (layoutContainer) {
        gsap.fromTo(
          layoutContainer,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1.5,
            ease: "power3.out",
            scrollTrigger: {
              trigger: layoutContainer,
              start: "top 90%",
              toggleActions: "play none none none",
            },
          },
        );
      }
    }, sectionRef);

    return () => context.revert();
  }, []);

  if (!galleryImages || galleryImages.length < 2) return null;

  const leftImage = galleryImages[currentSlide];
  const rightImageIndex = (currentSlide + 1) % galleryImages.length;
  const rightImage = galleryImages[rightImageIndex];

  // Format images for Lightbox
  const slides = galleryImages.map((img) => ({ src: img.src }));

  return (
    <section
      ref={sectionRef}
      className="w-full bg-background-main py-[40px] sm:py-[60px] lg:py-[86px]"
    >
      <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-[30px] sm:gap-[40px] lg:gap-[50px] justify-start items-center w-full">
          <h2
            ref={titleRef}
            className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-hero font-[Parkinsans] font-bold  text-center text-[#343163]"
          >
            Gallery
          </h2>

          <div className="relative w-full gallery-layout-container">
            <div className="flex flex-col lg:flex-row justify-start items-end gap-4 lg:gap-0 w-full">
              {/* Left Gallery Item */}
              <div className="flex flex-col gap-[15px] lg:gap-[20px] justify-start items-center w-full lg:w-[48%]">
                <img
                  src={leftImage?.src}
                  alt={leftImage?.title}
                  onClick={() => openLightbox(currentSlide)}
                  className="w-full h-[300px] sm:h-[450px] lg:h-[582px] object-cover rounded-[20px] cursor-zoom-in transition-transform duration-300 hover:scale-[1.01]"
                />
                <div className="flex flex-col gap-[8px] justify-start items-center w-full border-t-[1px] border-solid border-accent-text pt-[10px]">
                  <div className="flex flex-row justify-between items-center w-full">
                    <span className="text-[16px] lg:text-[20px] font-[Parkinsans] text-accent-muted">
                      {leftImage?.subtitle}
                    </span>
                    <h3 className="text-[18px] lg:text-[24px] font-[Parkinsans] text-accent-text">
                      {leftImage?.title}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Right Gallery Item */}
              <div className="flex flex-col-reverse md:flex-col justify-center items-end w-full lg:w-[52%] lg:mt-[100px] gap-4">
                <div className="flex flex-row justify-between md:justify-end items-center gap-[12px] w-full mt-[20px]">
                  <button
                    onClick={prevGallerySlide}
                    className="flex items-center justify-center w-[50px] h-[50px] lg:w-[80px] lg:h-[80px] border border-[#343163] rounded-full hover:bg-accent-text hover:invert transition-all"
                  >
                    <img
                      src="/assets/icon/leftArrow.png"
                      alt="Prev"
                      className="w-[10px] md:w-auto"
                    />
                  </button>
                  <button
                    onClick={nextGallerySlide}
                    className="flex items-center justify-center w-[50px] h-[50px] lg:w-[80px] lg:h-[80px] border border-[#343163] rounded-full hover:bg-accent-text hover:invert transition-all"
                  >
                    <img
                      src="/assets/icon/rightArrow.png"
                      alt="Next"
                      className="w-[10px] md:w-auto"
                    />
                  </button>
                </div>

                <div className="hidden md:flex flex-col gap-[15px] justify-start items-center w-full lg:w-[96%]">
                  <img
                    src={rightImage?.src}
                    alt={rightImage?.title}
                    onClick={() => openLightbox(rightImageIndex)}
                    className="w-full h-[200px] sm:h-[240px] lg:h-[274px] object-cover rounded-2xl cursor-zoom-in transition-transform duration-300 hover:scale-[1.02]"
                  />
                  <div className="flex flex-col gap-[8px] justify-start items-center w-full border-t-[1px] border-solid border-accent-text pt-[10px]">
                    <div className="flex flex-row justify-between items-center w-full">
                      <span className="text-[16px] lg:text-[20px] font-[Parkinsans] text-accent-muted">
                        {rightImage?.subtitle}
                      </span>
                      <h3 className="text-[18px] lg:text-[24px] font-[Parkinsans] text-accent-text">
                        {rightImage?.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Component */}
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
      />
    </section>
  );
};

export default GallerySection;
